
'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';

import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  BriefcaseBusiness,
  CarFront,
  MapPin,
  MessageCircle,
  Plane,
  ShieldCheck,
  Star,
  Users,
  Route,
  CalendarDays,
  Clock3,
  Building2,
  BookOpenText,
  KeyRound,
  Luggage,
} from 'lucide-react';

const WA_NUMBER = '6287786556067';

function createWhatsAppUrl(vehicle = 'Rental Mobil Jakarta') {
  const message = `Halo NAYVERA INDO TRANSPORT

Saya ingin menanyakan ketersediaan rental mobil Jakarta.

DATA PEMESANAN

Nama pemesan:
Kendaraan yang diminati: ${vehicle}
Jenis sewa: Dengan Driver / Lepas Kunci
Tanggal mulai:
Jam mulai:
Tanggal selesai:
Jam selesai:
Lokasi penjemputan:
Tujuan / rute:
Jumlah penumpang:

Mohon informasi:
• Ketersediaan kendaraan
• Harga dan pilihan paket
• Durasi serta fasilitas sewa
• Syarat dan ketentuan
• Biaya tambahan jika ada

Terima kasih.`;

  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
}

/* =========================================================
   ARMADA DAN HARGA
========================================================= */

const fleet = [
  {
    name: 'Toyota Agya / Daihatsu Ayla',
    category: 'City Car',
    image: '/assets/images/AYLA DAN AGYA.jpg',
    capacity: '5–6 Penumpang',
    price: 'Rp 298.000',
    priceLabel: '12 jam · lepas kunci',
    description:
      'Pilihan ekonomis untuk aktivitas harian, perjalanan dalam kota, kebutuhan pribadi, dan mobilitas praktis di Jakarta.',
  },
  {
    name: 'Toyota Avanza',
    category: 'Family MPV',
    image: '/assets/images/Avanza.jpeg',
    capacity: '5–6 Penumpang',
    price: 'Rp 598.000',
    priceLabel: 'Per tanggal · lepas kunci',
    description:
      'Mobil keluarga serbaguna untuk aktivitas perkotaan, kunjungan, perjalanan bersama, dan kebutuhan sehari-hari.',
  },
  {
    name: 'Innova Zenix G',
    category: 'Executive MPV',
    image: '/assets/images/ZENIX G.webp',
    capacity: '6–7 Penumpang',
    price: 'Rp 850.000',
    priceLabel: '12 jam · lepas kunci',
    description:
      'Pilihan untuk keluarga, perjalanan bisnis, aktivitas harian, dan perjalanan bersama dengan kabin yang lega.',
  },
  {
    name: 'Toyota Innova Reborn',
    category: 'Executive MPV',
    image: '/assets/images/REBORN.jpeg',
    capacity: '6–7 Penumpang',
    price: 'Rp 750.000',
    priceLabel: '12 jam · lepas kunci',
    description:
      'Cocok untuk perjalanan keluarga, tamu perusahaan, agenda bisnis, maupun kebutuhan perjalanan luar kota.',
  },
  {
    name: 'Toyota Fortuner',
    category: 'Premium SUV',
    image: '/assets/images/Fortuner.jpeg',
    capacity: '6–7 Penumpang',
    price: 'Rp 1.700.000',
    priceLabel: '12 jam · dengan driver',
    description:
      'SUV untuk kebutuhan executive, perjalanan bisnis, kunjungan klien, tamu perusahaan, dan perjalanan keluarga.',
  },
  {
    name: 'Toyota Hiace Premio',
    category: 'Premium Van',
    image: '/assets/images/Hiace-Premio.jpeg',
    capacity: '14 Penumpang',
    price: 'Rp 1.850.000',
    priceLabel: '12 jam · dengan driver',
    description:
      'Kendaraan untuk perjalanan rombongan, keluarga besar, corporate trip, wisata, dan kebutuhan event.',
  },
];

const services = [
  {
    icon: KeyRound,
    title: 'Sewa Mobil Lepas Kunci',
    text: 'Pilihan untuk pelanggan yang membutuhkan fleksibilitas berkendara sendiri. Ketersediaan unit dan persyaratan mengikuti ketentuan rental.',
  },
  {
    icon: Users,
    title: 'Sewa Mobil dengan Driver',
    text: 'Pilihan praktis untuk perjalanan keluarga, meeting, tamu perusahaan, airport transfer, maupun agenda seharian.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Corporate Mobility',
    text: 'Dukungan kendaraan untuk perjalanan dinas, kunjungan klien, meeting, dan kebutuhan transportasi perusahaan.',
  },
  {
    icon: Plane,
    title: 'Airport Transfer',
    text: 'Kebutuhan perjalanan menuju atau dari Bandara Internasional Soekarno-Hatta sesuai jadwal dan rute.',
  },
  {
    icon: Route,
    title: 'Perjalanan Luar Kota',
    text: 'Kendaraan untuk perjalanan dari Jakarta menuju kota tujuan lain berdasarkan rute dan ketentuan yang disepakati.',
  },
  {
    icon: CalendarDays,
    title: 'Harian dan Event',
    text: 'Untuk mobilitas harian, wisata, wedding, gathering, acara keluarga, dan kebutuhan transportasi khusus.',
  },
];

const areaGroups = [
  {
    title: 'Jakarta Pusat',
    areas: [
      'Menteng',
      'Thamrin',
      'Sudirman',
      'Tanah Abang',
      'Kemayoran',
      'Gambir',
      'Senayan',
    ],
  },
  {
    title: 'Jakarta Selatan',
    areas: [
      'Kuningan',
      'SCBD',
      'Pancoran',
      'Cilandak',
      'Kebayoran',
      'TB Simatupang',
      'Blok M',
    ],
  },
  {
    title: 'Jakarta Barat',
    areas: [
      'Puri Indah',
      'Kebon Jeruk',
      'Palmerah',
      'Tanjung Duren',
      'Grogol',
      'Cengkareng',
    ],
  },
  {
    title: 'Jakarta Timur',
    areas: [
      'Cawang',
      'Rawamangun',
      'Jatinegara',
      'Duren Sawit',
      'Pulo Gadung',
      'Halim',
    ],
  },
  {
    title: 'Jakarta Utara',
    areas: [
      'Kelapa Gading',
      'Pantai Indah Kapuk',
      'Pluit',
      'Sunter',
      'Ancol',
      'Tanjung Priok',
    ],
  },
];

const useCases = [
  {
    icon: CarFront,
    title: 'Mobilitas Harian',
    vehicle: 'Agya / Ayla / Avanza',
    text: 'Untuk aktivitas kerja, kebutuhan pribadi, kunjungan, dan perjalanan dalam kota.',
  },
  {
    icon: Users,
    title: 'Perjalanan Keluarga',
    vehicle: 'Avanza / Innova',
    text: 'Untuk agenda keluarga, perjalanan bersama, dan mobilitas dengan beberapa penumpang.',
  },
  {
    icon: Building2,
    title: 'Bisnis dan Perusahaan',
    vehicle: 'Innova / Fortuner',
    text: 'Untuk meeting, perjalanan dinas, kunjungan klien, dan kebutuhan tamu perusahaan.',
  },
  {
    icon: Luggage,
    title: 'Perjalanan Rombongan',
    vehicle: 'Hiace Premio',
    text: 'Untuk wisata, acara keluarga, perjalanan bersama rekan kerja, dan event.',
  },
];

const articles = [
  {
    number: '01',
    category: 'Panduan Rental',
    title: 'Cara Memilih Rental Mobil Jakarta Sesuai Kebutuhan',
    paragraphs: [
      'Sebelum memesan rental mobil Jakarta, tentukan terlebih dahulu tujuan perjalanan, jumlah penumpang, tanggal penggunaan, dan durasi sewa. Informasi tersebut membantu menentukan kendaraan yang sesuai.',
      'Untuk mobilitas perkotaan, city car dapat menjadi pilihan praktis. Untuk perjalanan keluarga, MPV seperti Avanza atau Innova dapat dipertimbangkan. Sementara itu, Hiace Premio dapat dipertimbangkan untuk perjalanan rombongan.',
      'Pastikan juga Anda memahami harga, durasi pemakaian, ketentuan penggunaan, biaya tambahan, dan kebijakan pengembalian kendaraan sebelum mengonfirmasi pesanan.',
    ],
  },
  {
    number: '02',
    category: 'Jenis Layanan',
    title: 'Rental Mobil Jakarta Lepas Kunci atau dengan Driver?',
    paragraphs: [
      'Sewa mobil lepas kunci memberikan fleksibilitas bagi pelanggan yang ingin mengemudikan kendaraan sendiri. Namun, layanan ini bergantung pada ketersediaan kendaraan dan pemenuhan persyaratan rental.',
      'Sewa mobil dengan driver dapat menjadi pilihan untuk perjalanan bisnis, agenda keluarga, penjemputan tamu, atau perjalanan ke bandara. Pelanggan dapat lebih fokus pada agenda selama perjalanan.',
      'Sebelum memesan, bandingkan kebutuhan perjalanan, durasi, rute, dan ketentuan masing-masing layanan. Tanyakan kepada admin apakah paket yang dipilih sudah mencakup driver, bahan bakar, parkir, tol, atau biaya lainnya.',
    ],
  },
  {
    number: '03',
    category: 'Panduan Armada',
    title: 'Memilih Mobil untuk Perjalanan Keluarga dan Bisnis di Jakarta',
    paragraphs: [
      'Kendaraan yang sesuai membantu perjalanan terasa lebih terencana. Pertimbangkan jumlah penumpang, barang bawaan, ruang kabin, serta agenda yang akan dilakukan.',
      'Agya atau Ayla dapat dipertimbangkan untuk mobilitas pribadi. Avanza dan Innova menawarkan pilihan untuk perjalanan bersama keluarga atau rekan kerja. Untuk kebutuhan executive, Fortuner dapat menjadi alternatif sesuai kebutuhan dan anggaran.',
      'Untuk rombongan yang membutuhkan tempat duduk lebih banyak, Hiace Premio dapat dipertimbangkan. Pastikan kapasitas dan ketersediaan unit dikonfirmasi saat melakukan pemesanan.',
    ],
  },
  {
    number: '04',
    category: 'Airport Transfer',
    title: 'Tips Memesan Mobil ke Bandara Soekarno-Hatta',
    paragraphs: [
      'Perjalanan menuju bandara sebaiknya direncanakan dengan memperhitungkan lokasi penjemputan, jadwal penerbangan, kondisi lalu lintas, dan waktu yang dibutuhkan untuk proses di bandara.',
      'Saat menghubungi penyedia rental, sampaikan tanggal, jam penjemputan, terminal jika sudah diketahui, jumlah penumpang, dan jumlah bagasi. Informasi ini membantu menentukan kendaraan dan jadwal perjalanan.',
      'Untuk penjemputan dari bandara, sertakan informasi kedatangan dan nomor penerbangan bila tersedia. Konfirmasikan kembali titik temu, biaya perjalanan, serta ketentuan waktu tunggu sebelum keberangkatan.',
    ],
  },
  {
    number: '05',
    category: 'Perjalanan Luar Kota',
    title: 'Hal yang Perlu Diperhatikan Sebelum Rental Mobil Luar Kota',
    paragraphs: [
      'Perjalanan luar kota membutuhkan perencanaan rute, durasi, dan kebutuhan kendaraan yang lebih terperinci. Sampaikan seluruh kota tujuan dan perkiraan jadwal kepada admin saat meminta penawaran.',
      'Tanyakan apakah layanan menggunakan driver, bagaimana ketentuan waktu pemakaian, dan apakah terdapat biaya tambahan untuk tol, parkir, penginapan driver, atau perjalanan di luar rute yang disepakati.',
      'Konfirmasikan total biaya dan detail layanan sebelum booking agar kebutuhan perjalanan dipahami oleh kedua pihak.',
    ],
  },
  {
    number: '06',
    category: 'Panduan Booking',
    title: 'Informasi yang Perlu Disiapkan Saat Booking Rental Mobil',
    paragraphs: [
      'Proses pemesanan dapat dimulai dengan menyampaikan kendaraan yang diinginkan, tanggal dan jam mulai, tanggal selesai, lokasi penjemputan, serta tujuan perjalanan.',
      'Sebutkan juga jumlah penumpang, kebutuhan bagasi, pilihan lepas kunci atau dengan driver, dan agenda khusus jika ada. Informasi yang lengkap membantu admin memeriksa kesesuaian unit dan layanan.',
      'Sebelum menyelesaikan pemesanan, pastikan harga, durasi, syarat pembayaran, ketentuan pembatalan, fasilitas, dan kemungkinan biaya tambahan sudah dijelaskan.',
    ],
  },
];

const faqs = [
  {
    question: 'Berapa harga sewa mobil Jakarta di NAYVERA?',
    answer:
      'Harga mengikuti jenis kendaraan dan ketentuan paket. Pada daftar armada halaman ini, Agya/Ayla tercantum Rp 298.000 untuk 12 jam lepas kunci, Avanza Rp 598.000 per tanggal lepas kunci, Innova Zenix G Rp 850.000 untuk 12 jam lepas kunci, Innova Reborn Rp 750.000 untuk 12 jam lepas kunci, Fortuner Rp 1.700.000 untuk 12 jam dengan driver, dan Hiace Premio Rp 1.850.000 untuk 12 jam dengan driver. Konfirmasikan kembali harga, durasi, dan ketentuan sebelum booking.',
  },
  {
    question: 'Apakah tersedia rental mobil Jakarta lepas kunci?',
    answer:
      'Ya, tersedia pilihan lepas kunci untuk unit tertentu. Ketersediaan dan persyaratan bergantung pada kendaraan serta tanggal penggunaan. Silakan konfirmasi kepada admin sebelum memesan.',
  },
  {
    question: 'Apakah tersedia rental mobil Jakarta dengan driver?',
    answer:
      'Ya, NAYVERA menyediakan pilihan kendaraan dengan driver untuk kebutuhan tertentu, termasuk perjalanan bisnis, keluarga, airport transfer, dan event. Konfirmasikan unit serta jadwal yang tersedia.',
  },
  {
    question: 'Apakah NAYVERA melayani Bandara Soekarno-Hatta?',
    answer:
      'Halaman ini menyediakan informasi layanan untuk kebutuhan perjalanan menuju maupun dari Bandara Soekarno-Hatta. Sampaikan jadwal, titik jemput, jumlah penumpang, dan tujuan kepada admin untuk konfirmasi layanan.',
  },
  {
    question: 'Apakah mobil bisa digunakan untuk perjalanan luar kota?',
    answer:
      'Perjalanan luar kota dapat dikonsultasikan berdasarkan tujuan, tanggal, durasi, kendaraan, dan jenis layanan. Admin akan membantu mengonfirmasi ketentuan serta biaya perjalanan.',
  },
  {
    question: 'Bagaimana cara memesan rental mobil Jakarta?',
    answer:
      'Klik tombol WhatsApp, lalu sampaikan kendaraan yang diminati, tanggal penggunaan, lokasi penjemputan, tujuan, jumlah penumpang, dan pilihan layanan. Tunggu konfirmasi ketersediaan serta detail pemesanan dari admin.',
  },
  {
    question: 'Apakah harga sudah termasuk tol, parkir, dan bahan bakar?',
    answer:
      'Komponen biaya dapat berbeda menurut jenis layanan dan paket. Tanyakan kepada admin apakah harga yang ditawarkan sudah mencakup bahan bakar, tol, parkir, driver, atau biaya lainnya.',
  },
  {
    question: 'Berapa jumlah penumpang yang dapat dibawa?',
    answer:
      'Kapasitas yang ditampilkan pada daftar armada adalah informasi awal. Jumlah penumpang aktual dan kebutuhan bagasi perlu disesuaikan dengan kendaraan serta dikonfirmasi sebelum pemesanan.',
  },
];

const reasons = [
  {
    icon: CarFront,
    title: 'Pilihan Armada',
    text: 'Pilihan kendaraan dari city car, MPV keluarga, SUV, hingga van untuk rombongan.',
  },
  {
    icon: ShieldCheck,
    title: 'Detail Layanan',
    text: 'Konsultasikan jenis sewa, durasi, ketentuan, dan kebutuhan perjalanan sebelum booking.',
  },
  {
    icon: MapPin,
    title: 'Area Perjalanan',
    text: 'Sampaikan titik penjemputan dan tujuan di Jakarta maupun rute luar kota.',
  },
  {
    icon: MessageCircle,
    title: 'Konsultasi WhatsApp',
    text: 'Kirim kebutuhan perjalanan agar admin dapat membantu mengecek ketersediaan unit.',
  },
];

export default function SewaMobilJakartaPage() {
  return (
    <>
      <Navbar />

      <main className="overflow-hidden bg-background text-foreground">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary text-white">
          <div className="absolute inset-0">
            <AppImage
              src="/assets/images/Armada Alphard HEV NAYVERA.jpeg"
              alt="Armada premium NAYVERA untuk kebutuhan transportasi Jakarta"
              fill
              priority
              className="object-cover object-center opacity-35"
              sizes="100vw"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/95 to-primary/60" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
          </div>

          <div className="relative z-10 mx-auto max-w-screen-2xl px-4 pb-20 pt-32 sm:px-6 sm:pb-24 lg:px-8 xl:px-10">
            <div className="grid w-full items-center gap-12 lg:grid-cols-[1.15fr_0.85fr]">
              <div className="max-w-3xl">
                <div className="mb-7 flex items-center gap-3">
                  <span className="h-px w-10 bg-accent" />
                  <span className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
                    NAYVERA INDO TRANSPORT
                  </span>
                </div>

                <h1 className="text-4xl font-black leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                  Sewa Mobil Jakarta
                  <span className="mt-2 block text-gradient-gold">
                    Mulai 300 Ribuan
                  </span>
                </h1>

                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/75 sm:text-xl">
                  Rental mobil Jakarta untuk mobilitas harian, perjalanan
                  bisnis, keluarga, airport transfer, event, hingga perjalanan
                  luar kota. Tersedia pilihan kendaraan sesuai kebutuhan.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <a
                    href={createWhatsAppUrl('Rental Mobil Jakarta')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-7 py-4 font-bold"
                  >
                    Konsultasi dan Booking
                    <MessageCircle size={18} />
                  </a>

                  <Link
                    href="#pilihan-armada"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-7 py-4 font-bold transition hover:bg-white/10"
                  >
                    Lihat Armada
                    <ArrowRight size={18} />
                  </Link>
                </div>

                <div className="mt-10 grid max-w-2xl grid-cols-2 gap-5 sm:grid-cols-4">
                  {[
                    ['City Car', 'Mobilitas praktis'],
                    ['MPV', 'Keluarga dan bisnis'],
                    ['Dengan Driver', 'Sesuai unit tersedia'],
                    ['Airport', 'Soekarno-Hatta'],
                  ].map(([title, subtitle]) => (
                    <div key={title} className="border-l border-accent/50 pl-3">
                      <div className="font-bold text-accent">{title}</div>
                      <div className="mt-1 text-xs leading-relaxed text-white/55">
                        {subtitle}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:pl-4">
                <div className="rounded-3xl border border-white/10 bg-black/25 p-6 shadow-2xl backdrop-blur-md sm:p-8">
                  <div className="mb-7 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent">
                        Pilihan layanan
                      </p>
                      <h2 className="mt-2 text-2xl font-extrabold text-white">
                        Satu kebutuhan, banyak pilihan
                      </h2>
                    </div>
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent/10 text-accent">
                      <CarFront size={24} />
                    </div>
                  </div>

                  <div className="space-y-5">
                    {[
                      {
                        icon: Clock3,
                        title: 'Harian dan sesuai durasi',
                        text: 'Tentukan jadwal dan lama pemakaian kendaraan.',
                      },
                      {
                        icon: BriefcaseBusiness,
                        title: 'Business dan corporate',
                        text: 'Untuk meeting, perjalanan dinas, dan tamu perusahaan.',
                      },
                      {
                        icon: Plane,
                        title: 'Airport transfer',
                        text: 'Konsultasikan perjalanan menuju atau dari Soekarno-Hatta.',
                      },
                    ].map((item) => {
                      const Icon = item.icon;

                      return (
                        <div key={item.title} className="flex gap-4">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
                            <Icon size={21} />
                          </div>
                          <div>
                            <h3 className="font-bold text-white">{item.title}</h3>
                            <p className="mt-1 text-sm leading-relaxed text-white/55">
                              {item.text}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <a
                    href={createWhatsAppUrl('Rental Mobil Jakarta')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-5 py-3.5 font-bold text-accent-foreground transition hover:opacity-90"
                  >
                    Tanyakan Ketersediaan
                    <ArrowRight size={17} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INTRO */}
        <section className="bg-background py-20 sm:py-24">
          <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.75fr_1.25fr] lg:gap-20 lg:px-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                Rental Mobil Jakarta
              </span>
              <h2 className="mt-4 text-3xl font-black leading-tight text-foreground sm:text-4xl lg:text-5xl">
                Bergerak di Jakarta,
                <span className="mt-1 block text-gradient-gold">
                  Lebih Terencana
                </span>
              </h2>
            </div>

            <div className="space-y-5">
              <p className="text-lg leading-relaxed text-muted-foreground">
                Jakarta memiliki beragam kebutuhan transportasi, mulai dari
                aktivitas harian, perjalanan keluarga, meeting, kunjungan
                klien, hingga antar jemput bandara. Pemilihan kendaraan yang
                sesuai membantu perjalanan direncanakan dengan lebih baik.
              </p>
              <p className="leading-relaxed text-muted-foreground">
                NAYVERA INDO TRANSPORT menyediakan pilihan kendaraan dari city
                car hingga MPV, SUV, dan van untuk rombongan. Jenis layanan,
                durasi, dan ketersediaan unit dapat dikonsultasikan sebelum
                pemesanan.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                {[
                  'Harian',
                  'Lepas Kunci',
                  'Dengan Driver',
                  'Corporate',
                  'Airport Transfer',
                  'Luar Kota',
                ].map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="bg-primary py-20 text-white sm:py-24">
          <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8 xl:px-10">
            <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                  Layanan NAYVERA
                </span>
                <h2 className="mt-3 text-3xl font-black sm:text-4xl lg:text-5xl">
                  Satu Partner untuk
                  <span className="block text-gradient-gold">
                    Berbagai Perjalanan
                  </span>
                </h2>
              </div>
              <p className="max-w-md leading-relaxed text-white/55">
                Sampaikan kebutuhan perjalanan agar jenis kendaraan, durasi,
                dan layanan dapat dikonfirmasi sebelum booking.
              </p>
            </div>

            <div className="grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
              {services.map((service) => {
                const Icon = service.icon;

                return (
                  <article
                    key={service.title}
                    className="bg-primary p-7 transition-colors hover:bg-white/[0.04] sm:p-8"
                  >
                    <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10 text-accent">
                      <Icon size={24} />
                    </div>
                    <h3 className="mb-3 text-xl font-extrabold">
                      {service.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-white/55">
                      {service.text}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* PILIH BERDASARKAN KEBUTUHAN */}
        <section className="bg-muted/30 py-20 sm:py-24">
          <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8 xl:px-10">
            <div className="mx-auto mb-14 max-w-3xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                Pilih Sesuai Kebutuhan
              </span>
              <h2 className="mt-3 text-3xl font-black text-foreground sm:text-4xl lg:text-5xl">
                Mobil yang Tepat untuk
                <span className="text-gradient-gold"> Perjalanan Anda</span>
              </h2>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Pertimbangkan jumlah penumpang, karakter perjalanan, dan
                kebutuhan layanan sebelum memilih armada.
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {useCases.map((item) => {
                const Icon = item.icon;

                return (
                  <article
                    key={item.title}
                    className="rounded-3xl border border-border bg-card p-6 sm:p-7"
                  >
                    <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10 text-accent">
                      <Icon size={23} />
                    </div>
                    <h3 className="mb-2 text-xl font-extrabold text-foreground">
                      {item.title}
                    </h3>
                    <p className="mb-3 text-sm font-bold text-accent">
                      {item.vehicle}
                    </p>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {item.text}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ARMADA DAN HARGA */}
        <section
          id="pilihan-armada"
          className="scroll-mt-24 bg-background py-20 sm:py-24"
        >
          <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8 xl:px-10">
            <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                  Armada NAYVERA
                </span>
                <h2 className="mt-3 text-3xl font-black text-foreground sm:text-4xl lg:text-5xl">
                  Pilihan Kendaraan
                  <span className="block text-gradient-gold">
                    untuk Jakarta
                  </span>
                </h2>
                <p className="mt-5 leading-relaxed text-muted-foreground">
                  Berikut pilihan armada dan harga sesuai informasi yang
                  tersedia. Konfirmasikan kembali ketersediaan, durasi,
                  fasilitas, serta ketentuan paket sebelum booking.
                </p>
              </div>

              <Link
                href="/fleet-armada-page"
                className="inline-flex shrink-0 items-center gap-2 font-bold text-foreground transition hover:text-accent"
              >
                Lihat Seluruh Armada
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {fleet.map((vehicle) => (
                <article
                  key={vehicle.name}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition duration-300 hover:-translate-y-1 hover:border-accent/50 hover:shadow-xl"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                    <AppImage
                      src={vehicle.image}
                      alt={`${vehicle.name} untuk rental mobil Jakarta NAYVERA`}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                    <div className="absolute left-4 top-4 rounded-full bg-primary/90 px-3 py-1.5 text-xs font-bold text-white backdrop-blur">
                      {vehicle.category}
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="text-xl font-extrabold text-foreground">
                      {vehicle.name}
                    </h3>

                    <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                      <Users size={16} className="shrink-0 text-accent" />
                      <span>{vehicle.capacity}</span>
                    </div>

                    <p className="mt-4 flex-1 text-sm leading-7 text-muted-foreground">
                      {vehicle.description}
                    </p>

                    <div className="mt-6 border-t border-border pt-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">
                            Harga sewa
                          </p>
                          <p className="mt-1 text-xl font-extrabold text-foreground">
                            {vehicle.price}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {vehicle.priceLabel}
                          </p>
                        </div>

                        <div className="flex shrink-0 flex-col gap-2">
                          <a
                            href={createWhatsAppUrl(vehicle.name)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Booking ${vehicle.name} melalui WhatsApp`}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-accent px-5 py-2.5 text-center text-xs font-bold text-accent-foreground transition hover:opacity-90"
                          >
                            Booking
                            <ArrowRight size={15} />
                          </a>
                          <a
                            href={createWhatsAppUrl(vehicle.name)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Tanyakan ketersediaan ${vehicle.name} melalui WhatsApp`}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/40 px-5 py-2.5 text-center text-xs font-bold text-emerald-600 transition hover:bg-emerald-500/10"
                          >
                            <MessageCircle size={15} />
                            WhatsApp
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <p className="mx-auto mt-7 max-w-3xl text-center text-xs leading-relaxed text-muted-foreground">
              Harga dan paket mengikuti informasi yang tercantum pada halaman
              ini. Pastikan durasi, tanggal penggunaan, fasilitas, dan biaya
              tambahan dikonfirmasi kepada admin sebelum melakukan pemesanan.
            </p>

            <div className="mt-10 flex justify-center">
              <Link
                href="/fleet-armada-page"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-7 py-4 font-bold text-white transition hover:opacity-90"
              >
                Lihat Seluruh Armada
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        {/* CORPORATE */}
        <section className="bg-primary py-20 text-white sm:py-24">
          <div className="mx-auto grid max-w-screen-2xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8 xl:px-10">
            <div className="relative">
              <div className="absolute -inset-5 rounded-full bg-accent/10 blur-3xl" />
              <div className="relative h-[340px] overflow-hidden rounded-[2rem] border border-white/10 sm:h-[420px]">
                <AppImage
                  src="/assets/images/ALPHARD HEV.jpeg"
                  alt="Toyota Alphard HEV untuk kebutuhan transportasi executive NAYVERA"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-6 pt-16">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                    Business Mobility
                  </span>
                  <p className="mt-2 text-xl font-extrabold text-white">
                    Transportasi untuk Agenda Profesional
                  </p>
                </div>
              </div>
            </div>

            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                Business dan Executive
              </span>
              <h2 className="mt-4 text-3xl font-black leading-tight sm:text-4xl lg:text-5xl">
                Mobilitas Bisnis
                <span className="block text-gradient-gold">
                  Lebih Terencana
                </span>
              </h2>
              <p className="mb-7 mt-6 leading-relaxed text-white/65">
                Untuk meeting, perjalanan dinas, kunjungan klien, atau
                kebutuhan transportasi tamu perusahaan, kendaraan dengan
                driver dapat menjadi pilihan praktis. Sampaikan jadwal dan
                agenda agar kebutuhan kendaraan dapat dikonsultasikan.
              </p>

              <div className="mb-8 space-y-4">
                {[
                  'Informasikan titik penjemputan dan jadwal',
                  'Pilih kendaraan berdasarkan kebutuhan perjalanan',
                  'Konfirmasikan durasi dan detail biaya',
                  'Konsultasikan melalui WhatsApp NAYVERA',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2
                      size={19}
                      className="mt-0.5 shrink-0 text-accent"
                    />
                    <span className="text-sm text-white/75">{item}</span>
                  </div>
                ))}
              </div>

              <a
                href={createWhatsAppUrl('Kebutuhan Corporate / Executive')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2 rounded-xl px-7 py-4 font-bold"
              >
                Konsultasi Corporate
                <MessageCircle size={18} />
              </a>
            </div>
          </div>
        </section>

        {/* AIRPORT TRANSFER */}
        <section className="bg-background py-20 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="overflow-hidden rounded-[2rem] border border-border bg-muted/30">
              <div className="grid lg:grid-cols-[1fr_0.8fr]">
                <div className="p-8 sm:p-12 lg:p-14">
                  <div className="mb-7 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent">
                    <Plane size={26} />
                  </div>

                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                    Airport Transfer
                  </span>
                  <h2 className="mb-5 mt-3 text-3xl font-black text-foreground sm:text-4xl">
                    Jakarta
                    <span className="text-gradient-gold"> ↔ </span>
                    Soekarno-Hatta
                  </h2>
                  <p className="mb-7 leading-relaxed text-muted-foreground">
                    Membutuhkan kendaraan menuju bandara, penjemputan dari
                    bandara, atau perjalanan lanjutan ke Jakarta? Sampaikan
                    jadwal penerbangan, titik jemput, jumlah penumpang, dan
                    kebutuhan bagasi agar pilihan kendaraan dapat
                    dikonsultasikan.
                  </p>

                  <div className="mb-8 grid gap-3 sm:grid-cols-2">
                    {[
                      'Jakarta ke bandara',
                      'Bandara ke Jakarta',
                      'Perjalanan bisnis',
                      'Keluarga dan tamu',
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <CheckCircle2 size={17} className="text-accent" />
                        <span className="text-sm font-semibold text-foreground">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>

                  <a
                    href={createWhatsAppUrl('Airport Transfer Soekarno-Hatta')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-2 rounded-xl px-7 py-4 font-bold"
                  >
                    Tanya Airport Transfer
                    <ArrowRight size={18} />
                  </a>
                </div>

                <div className="relative min-h-[320px] lg:min-h-full">
                  <AppImage
                    src="/assets/images/Armada Hiace Nayvera.jpeg"
                    alt="Armada Hiace NAYVERA untuk kebutuhan perjalanan bandara"
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 100vw, 40vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/30 via-transparent to-transparent lg:bg-gradient-to-r lg:from-muted/50 lg:via-transparent lg:to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AREA LAYANAN */}
        <section className="bg-primary py-20 text-white sm:py-24">
          <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8 xl:px-10">
            <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-20">
              <div>
                <div className="mb-5 flex items-center gap-3">
                  <MapPin size={18} className="text-accent" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                    Area Layanan
                  </span>
                </div>
                <h2 className="mb-5 text-3xl font-black leading-tight sm:text-4xl lg:text-5xl">
                  Melayani Berbagai
                  <span className="block text-gradient-gold">
                    Kawasan Jakarta
                  </span>
                </h2>
                <p className="leading-relaxed text-white/55">
                  Sampaikan lokasi penjemputan dan tujuan perjalanan saat
                  menghubungi admin. Layanan untuk setiap kawasan mengikuti
                  jadwal dan ketersediaan yang dikonfirmasi.
                </p>
                <a
                  href={createWhatsAppUrl('Kebutuhan Area Jakarta')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary mt-7 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 font-bold"
                >
                  Tanyakan Area Layanan
                  <ArrowRight size={17} />
                </a>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {areaGroups.map((group) => (
                  <article
                    key={group.title}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                  >
                    <h3 className="mb-4 text-lg font-bold text-white">
                      {group.title}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {group.areas.map((area) => (
                        <span
                          key={area}
                          className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/65"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </article>
                ))}

                <div className="rounded-2xl border border-accent/20 bg-accent/5 p-6 sm:col-span-2">
                  <div className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-accent">
                    Area Sekitar dan Rute Lanjutan
                  </div>
                  <p className="leading-relaxed text-white/75">
                    Untuk kebutuhan perjalanan ke Tangerang, Tangerang
                    Selatan, Bandara Soekarno-Hatta, atau kota lain di luar
                    Jakarta, sampaikan rute lengkap agar layanan dan biayanya
                    dapat dikonfirmasi terlebih dahulu.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ALASAN MEMILIH NAYVERA */}
        <section className="bg-background py-20 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto mb-12 max-w-3xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                Kenapa NAYVERA
              </span>
              <h2 className="mt-3 text-3xl font-black text-foreground sm:text-4xl">
                Informasi Jelas Sebelum
                <span className="text-gradient-gold"> Perjalanan Dimulai</span>
              </h2>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Pilih kendaraan berdasarkan kebutuhan dan pastikan detail
                layanan dikonfirmasi sebelum menyelesaikan pemesanan.
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {reasons.map((item) => {
                const Icon = item.icon;

                return (
                  <article
                    key={item.title}
                    className="rounded-3xl border border-border bg-card p-6"
                  >
                    <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10 text-accent">
                      <Icon size={22} />
                    </div>
                    <h3 className="mb-2 font-extrabold text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {item.text}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* PANDUAN BOOKING */}
        <section className="bg-muted/30 py-20 sm:py-24">
          <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.75fr_1.25fr] lg:gap-20 lg:px-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                Cara Booking
              </span>
              <h2 className="mb-5 mt-3 text-3xl font-black text-foreground sm:text-4xl">
                Kirim Detail
                <span className="block text-gradient-gold">
                  Kebutuhan Anda
                </span>
              </h2>
              <p className="mb-7 leading-relaxed text-muted-foreground">
                Mulai dengan mengirim informasi perjalanan melalui WhatsApp.
                Admin dapat membantu mengecek ketersediaan unit dan menjelaskan
                ketentuan sebelum pemesanan dikonfirmasi.
              </p>
              <a
                href={createWhatsAppUrl('Rental Mobil Jakarta')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2 rounded-xl px-7 py-4 font-bold"
              >
                Chat Admin NAYVERA
                <MessageCircle size={18} />
              </a>
            </div>

            <div className="space-y-3">
              {[
                {
                  number: '01',
                  title: 'Tentukan kendaraan',
                  text: 'Sampaikan kendaraan pilihan atau jenis perjalanan yang dibutuhkan.',
                },
                {
                  number: '02',
                  title: 'Kirim tanggal dan durasi',
                  text: 'Sertakan tanggal mulai, jam penjemputan, dan tanggal selesai.',
                },
                {
                  number: '03',
                  title: 'Jelaskan rute',
                  text: 'Berikan lokasi penjemputan, tujuan, dan jumlah penumpang.',
                },
                {
                  number: '04',
                  title: 'Pilih jenis sewa',
                  text: 'Konsultasikan pilihan lepas kunci atau dengan driver sesuai unit.',
                },
                {
                  number: '05',
                  title: 'Konfirmasi detail',
                  text: 'Pastikan harga, fasilitas, ketentuan, dan biaya tambahan sebelum booking.',
                },
              ].map((step) => (
                <article
                  key={step.number}
                  className="flex gap-5 rounded-2xl border border-border bg-card p-5"
                >
                  <div className="w-8 shrink-0 text-lg font-black text-accent">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="mb-1 font-extrabold text-foreground">
                      {step.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {step.text}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ARTIKEL SEO */}
        <section className="bg-background py-20 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 max-w-3xl">
              <div className="mb-4 flex items-center gap-3">
                <BookOpenText size={21} className="text-accent" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                  Panduan Perjalanan
                </span>
              </div>
              <h2 className="text-3xl font-black text-foreground sm:text-4xl lg:text-5xl">
                Artikel dan Tips
                <span className="block text-gradient-gold">
                  Rental Mobil Jakarta
                </span>
              </h2>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Pelajari hal-hal yang perlu diperhatikan sebelum memilih
                kendaraan, menentukan jenis sewa, dan merencanakan perjalanan.
              </p>
            </div>

            <div className="space-y-5">
              {articles.map((article) => (
                <article
                  key={article.number}
                  className="rounded-3xl border border-border bg-card p-6 sm:p-8"
                >
                  <div className="mb-4 flex flex-wrap items-center gap-3">
                    <span className="text-2xl font-black text-accent/60">
                      {article.number}
                    </span>
                    <span className="rounded-full bg-accent/10 px-3 py-1.5 text-xs font-bold text-accent">
                      {article.category}
                    </span>
                  </div>
                  <h3 className="mb-5 text-xl font-extrabold leading-snug text-foreground sm:text-2xl">
                    {article.title}
                  </h3>
                  <div className="space-y-4">
                    {article.paragraphs.map((paragraph, index) => (
                      <p
                        key={`${article.number}-${index}`}
                        className="text-sm leading-7 text-muted-foreground sm:text-base"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-muted/30 py-20 sm:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
                FAQ
              </span>
              <h2 className="mt-3 text-3xl font-black text-foreground sm:text-4xl">
                Pertanyaan Seputar
                <span className="block text-gradient-gold">
                  Sewa Mobil Jakarta
                </span>
              </h2>
              <p className="mx-auto mt-5 max-w-2xl leading-relaxed text-muted-foreground">
                Informasi awal untuk membantu Anda menyiapkan kebutuhan
                perjalanan sebelum menghubungi admin.
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq) => (
                <details
                  key={faq.question}
                  className="group overflow-hidden rounded-2xl border border-border bg-card"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 px-5 py-5 font-bold text-foreground sm:px-6">
                    <span>{faq.question}</span>
                    <ChevronDown
                      size={19}
                      className="shrink-0 text-accent transition-transform group-open:rotate-180"
                    />
                  </summary>
                  <div className="border-t border-border px-5 pb-5 pt-4 text-sm leading-7 text-muted-foreground sm:px-6">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative overflow-hidden bg-primary py-20 text-white sm:py-24">
          <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />
          <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-accent/5 blur-3xl" />

          <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
            <div className="mx-auto mb-7 flex h-14 w-14 items-center justify-center rounded-2xl border border-accent/20 bg-accent/10">
              <Star size={25} className="text-accent" />
            </div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
              NAYVERA INDO TRANSPORT
            </span>
            <h2 className="mb-5 mt-4 text-3xl font-black leading-tight sm:text-4xl lg:text-5xl">
              Butuh Rental Mobil
              <span className="block text-gradient-gold">di Jakarta?</span>
            </h2>
            <p className="mx-auto mb-8 max-w-2xl leading-relaxed text-white/60">
              Kirim kendaraan pilihan, tanggal penggunaan, titik penjemputan,
              tujuan, dan jumlah penumpang. Hubungi NAYVERA untuk
              mengonsultasikan ketersediaan serta detail paket sewa.
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <a
                href={createWhatsAppUrl('Rental Mobil Jakarta')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 font-bold"
              >
                Hubungi Admin NAYVERA
                <MessageCircle size={18} />
              </a>
              <Link
                href="#pilihan-armada"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-8 py-4 font-bold transition hover:bg-white/5"
              >
                Lihat Harga Armada
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
    </>
  );
}
